/**
 * Elternbrief-Leser (27.10.2026, Wettbewerbsbeobachtung Runde 2).
 *
 * Ein Zettel aus Schule oder Kita wird **auf dem Gerät** gelesen (Texterkennung
 * im Browser); diese reine Funktion macht aus dem Text Vorschläge für Termine
 * und Aufgaben — „Mi 4.11. Wandertag · 3 € mitgeben · Turnbeutel“. Sie läuft
 * genauso auf eingetipptem oder eingefügtem Text.
 *
 * Ehrlich statt geraten:
 * - Was unsicher ist, trägt einen Zweifel (`doubts`) und `confidence: 'low'`
 *   bzw. `'medium'` — es wird nie still weggelassen und nie still „repariert“.
 * - Vergangenes und das Briefdatum werden nicht vorgeschlagen, stehen aber in
 *   `skipped` mit Grund.
 * - Fehlt ein Datum, ist es `null` (Zweifel `noDate`), nicht „heute“.
 * - Geld nur über `parseAmount` (Cent als ganze Zahl); was es nicht lesen
 *   kann, bleibt `null` mit Zweifel `amountUnclear`.
 * - Titel setzt die App in ihrer Sprache zusammen (`LetterTitle`); aus dem
 *   Brief übernommen werden nur Wörter, die dort stehen.
 *
 * Nicht erkannt werden (bewusst): „morgen“/„übermorgen“ (der Brief wird nicht
 * am Tag gelesen, an dem er geschrieben wurde) und Wiederholungen („jeden
 * Freitag“) — beides bleibt im erkannten Text sichtbar.
 */

import { parseAmount } from './costs.js';
import { daysBetween } from './consumption.js';
import { weekdayOf } from './schedule.js';
import { addDays, isoDateInZone } from './time.js';
import type { IsoDate, WallTime } from './time.js';

export const LETTER_DOUBTS = [
  /** Ziffern aus der Texterkennung korrigiert („5.ll.“ → „5.11.“). */
  'ocrFixed',
  /** Nur ein Tag („am 12.“) — der Monat ist ergänzt. */
  'monthGuessed',
  /** Wochentag und Datum passen nicht zusammen. */
  'weekdayMismatch',
  /** Nur ein Wochentag („bis Freitag“) — gerechnet ab heute. */
  'weekdayOnly',
  /** „nächste Woche Dienstag“ — gerechnet ab heute, nicht ab dem Briefdatum. */
  'relativeDay',
  /** Kein Datum gefunden. */
  'noDate',
  /** Das Datum stammt aus einem anderen Satz des Briefs. */
  'dateFromContext',
  /** Kein Stichwort gefunden, der Titel ist ein gekürzter Satz. */
  'titleGuessed',
  /** Ein Betrag, der sich nicht sicher lesen lässt. */
  'amountUnclear',
] as const;
export type LetterDoubt = (typeof LETTER_DOUBTS)[number];

/** Diese Zweifel machen einen Vorschlag „unsicher“ — die App wählt ihn nicht vor. */
const LOW_DOUBTS: ReadonlySet<LetterDoubt> = new Set([
  'monthGuessed',
  'weekdayMismatch',
  'weekdayOnly',
  'relativeDay',
  'noDate',
  'amountUnclear',
]);

export type LetterConfidence = 'high' | 'medium' | 'low';

/**
 * Woraus der Titel entsteht. `name` und `sentence` sind Text aus dem Brief,
 * die übrigen setzt die App in ihrer Sprache zusammen.
 */
export type LetterTitle =
  | { type: 'name'; text: string }
  | { type: 'sentence'; text: string }
  | { type: 'money'; subject: string | null; transfer: boolean }
  | { type: 'bring' }
  | { type: 'deadline'; subject: string | null };

export interface LetterSuggestion {
  kind: 'event' | 'task';
  title: LetterTitle;
  date: IsoDate | null;
  time: WallTime | null;
  /** Aus „8:15 – 12:30 Uhr“. */
  durationMin: number | null;
  /** Mehrtägig („10.–14.11.“). */
  endDate: IsoDate | null;
  amountCents: number | null;
  /** Mitbringliste. */
  items: string[];
  confidence: LetterConfidence;
  doubts: LetterDoubt[];
  /** Der Satz aus dem Brief, aus dem der Vorschlag stammt. */
  sourceLine: string;
}

export type LetterSkipReason = 'past' | 'letterDate';

export interface LetterSkipped {
  reason: LetterSkipReason;
  date: IsoDate;
  sourceLine: string;
}

export interface LetterReading {
  suggestions: LetterSuggestion[];
  /** Erkannt, aber nicht vorgeschlagen — mit Grund, nie still. */
  skipped: LetterSkipped[];
}

/** Liegt ein Datum ohne Jahr weiter als so viele Tage zurück, ist das nächste Jahr gemeint. */
export const PAST_WINDOW_DAYS = 90;

// --- Wörter ------------------------------------------------------------------

const MONTH_NAMES: [RegExp, number][] = [
  [/^(?:Januar|Jänner|Jan)$/, 1],
  [/^(?:Februar|Feb)$/, 2],
  [/^(?:März|Maerz|Marz|Mär|Mrz)$/, 3],
  [/^(?:April|Apr)$/, 4],
  [/^Mai$/, 5],
  [/^(?:Juni|Jun)$/, 6],
  [/^(?:Juli|Jul)$/, 7],
  [/^(?:August|Aug)$/, 8],
  [/^(?:Septe(?:m|rn)ber|Sept|Sep)$/, 9],
  [/^(?:Oktober|Okt)$/, 10],
  [/^(?:Nove(?:m|rn)ber|Nov)$/, 11],
  [/^(?:Deze(?:m|rn)ber|Dez)$/, 12],
];
const MONTH =
  '(Januar|Jänner|Jan|Februar|Feb|März|Maerz|Marz|Mär|Mrz|April|Apr|Mai|Juni|Jun|Juli|Jul|August|Aug|Septe(?:m|rn)ber|Sept|Sep|Oktober|Okt|Nove(?:m|rn)ber|Nov|Deze(?:m|rn)ber|Dez)(?![a-zäöüß])';

const WEEKDAY_NAMES: [RegExp, number][] = [
  [/^(?:Montag|Mo)$/i, 1],
  [/^(?:Dienstag|Di)$/i, 2],
  [/^(?:Mittwoch|Mi)$/i, 3],
  [/^(?:Donnerstag|Do)$/i, 4],
  [/^(?:Freitag|Fr)$/i, 5],
  [/^(?:Samstag|Sonnabend|Sa)$/i, 6],
  [/^(?:Sonntag|So)$/i, 7],
];
const WD_FULL = '(Montag|Dienstag|Mittwoch|Donnerstag|Freitag|Samstag|Sonnabend|Sonntag)';
/** Vor einem Datum auch abgekürzt — „Mi 4.11.“, „Do., 13.11.“. */
const WD_PREFIX = `(?:(Montag|Dienstag|Mittwoch|Donnerstag|Freitag|Samstag|Sonnabend|Sonntag|Mo|Di|Mi|Do|Fr|Sa|So)\\.?,?\\s*(?:den\\s+)?)?`;
const DASH = '(?:-|–|—|bis(?:\\s+(?:zum|einschließlich))?)';

/** Stichwörter, die einen Termin benennen. */
const EVENT_WORDS = new Set([
  'fotograf',
  'ausflug',
  'exkursion',
  'zahnarzt',
  'schulzahnarzt',
  'theater',
  'kino',
  'museum',
  'gottesdienst',
  'aufführung',
  'konzert',
  'lesung',
  'impfung',
  'untersuchung',
  'elterngespräch',
  'elterngespräche',
  'sprechstunde',
  'ferien',
  'konferenz',
  'zeugnis',
  'zeugnisse',
  'zeugnisausgabe',
  'fasching',
  'karneval',
  'nikolaus',
  'schwimmen',
  'bücherei',
  'bibliothek',
  'flohmarkt',
  'basar',
  'kuchenverkauf',
  'elterncafé',
  'einschulung',
  'wanderung',
  'radtour',
  'schullandheim',
  'zeltlager',
  'übernachtung',
  'turnier',
  'wettbewerb',
  'bundesjugendspiele',
  'sportabzeichen',
  'elternversammlung',
  'laternelaufen',
]);
const EVENT_SUFFIXES = [
  'tag',
  'tage',
  'fest',
  'feier',
  'abend',
  'fahrt',
  'ausflug',
  'umzug',
  'woche',
  'nacht',
  'besuch',
  'unterricht',
  'probe',
  'turnier',
  'lauf',
  'ferien',
  'frühstück',
  'versammlung',
  'sitzung',
  'frei',
];
const NOT_EVENT = new Set([
  'montag',
  'dienstag',
  'donnerstag',
  'freitag',
  'samstag',
  'sonntag',
  'sonnabend',
  'nachmittag',
  'vormittag',
  'mittag',
  'vortag',
  'alltag',
  'werktag',
  'wochentag',
  'feiertage',
  'abend',
  'woche',
  'frei',
  'kostenfrei',
  'gebührenfrei',
  'eintrittsfrei',
  'barrierefrei',
  'einwandfrei',
  'beitrag',
  'vertrag',
  'antrag',
  'auftrag',
  'betrag',
  'ertrag',
  'nachtrag',
  'vorschlag',
]);

/** Substantive, die sagen, was bis zu einer Frist zurück muss. */
const SUBJECT_SUFFIXES = [
  'abschnitt',
  'zettel',
  'erklärung',
  'anmeldung',
  'formular',
  'bogen',
  'rückmeldung',
  'antrag',
  'bescheinigung',
  'attest',
  'nachweis',
  'unterlagen',
  'liste',
  'heft',
  'mappe',
  'ausweis',
  'pass',
  'vertrag',
  'unterschrift',
  'brief',
  'beitrag',
  'geld',
];

const ABBREVIATIONS = new Set([
  'z.',
  'b.',
  'z.b.',
  'u.',
  'a.',
  'u.a.',
  'bzw.',
  'ca.',
  'nr.',
  'std.',
  'evtl.',
  'ggf.',
  'inkl.',
  'str.',
  'tel.',
  'd.',
  'h.',
  'd.h.',
  'mo.',
  'di.',
  'mi.',
  'do.',
  'fr.',
  'sa.',
  'so.',
  'st.',
  'kl.',
  'frau',
  'hr.',
  'fr.',
]);

// --- Hilfen ------------------------------------------------------------------

function monthOfName(name: string): number | null {
  for (const [pattern, month] of MONTH_NAMES) if (pattern.test(name)) return month;
  return null;
}

function weekdayOfName(name: string | undefined): number | null {
  if (!name) return null;
  for (const [pattern, day] of WEEKDAY_NAMES) if (pattern.test(name)) return day;
  return null;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function validDate(year: number, month: number, day: number): IsoDate | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

function capitalize(word: string): string {
  return word.charAt(0).toLocaleUpperCase('de') + word.slice(1);
}

function clip(text: string, max = 200): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** Ein Datum ergänzen — oder sagen, dass es vorbei ist. */
interface Resolved {
  date: IsoDate;
  past: boolean;
  doubts: LetterDoubt[];
}

function resolveDate(
  day: number,
  month: number,
  year: number | null,
  weekday: number | null,
  today: IsoDate,
): Resolved | null {
  const doubts: LetterDoubt[] = [];
  const thisYear = Number(today.slice(0, 4));
  if (year !== null) {
    const full = year < 100 ? 2000 + year : year;
    const date = validDate(full, month, day);
    if (!date) return null;
    if (weekday !== null && weekdayOf(date) !== weekday) doubts.push('weekdayMismatch');
    return { date, past: date < today, doubts };
  }
  const current = validDate(thisYear, month, day);
  const next = validDate(thisYear + 1, month, day);
  if (!current && !next) return null;
  const fits = (date: IsoDate | null) =>
    date !== null && (weekday === null || weekdayOf(date) === weekday);

  if (current && current >= today) {
    if (weekday !== null && !fits(current)) doubts.push('weekdayMismatch');
    return { date: current, past: false, doubts };
  }
  // Schon vorbei: kurz zurück und passend → vorbei; sonst ist das nächste Jahr gemeint.
  if (current && daysBetween(current, today) <= PAST_WINDOW_DAYS && fits(current)) {
    return { date: current, past: true, doubts };
  }
  if (!next) return { date: current!, past: true, doubts };
  if (weekday !== null && !fits(next)) doubts.push('weekdayMismatch');
  return { date: next, past: false, doubts };
}

/** Nächster solcher Wochentag — `allowToday` für „bis Freitag“ am Freitag. */
function nextWeekday(today: IsoDate, weekday: number, allowToday: boolean): IsoDate {
  let offset = (weekday - weekdayOf(today) + 7) % 7;
  if (offset === 0 && !allowToday) offset = 7;
  return addDays(today, offset);
}

// --- Texterkennung ausbessern -------------------------------------------------

/**
 * Typische Ziffernfehler der Texterkennung in Datums- und Zeitangaben:
 * `l`, `I`, `|` für 1 und `O`/`o` für 0 — nur in Gebilden, die schon eine
 * echte Ziffer und einen Trenner haben („5.ll.“, „l2:3O“), nie in Wörtern.
 * Die Länge bleibt gleich, damit `fixed` den Stellen im Text entspricht.
 */
export function fixOcrDigits(text: string): { text: string; fixed: boolean[] } {
  const chars = [...text];
  const fixed = chars.map(() => false);
  // Auf Code-Einheiten rechnen — `chars` ist für BMP-Text deckungsgleich.
  if (chars.length !== text.length) return { text, fixed: new Array(text.length).fill(false) };
  const swap = (start: number, token: string) => {
    for (let index = 0; index < token.length; index += 1) {
      const char = token[index]!;
      const digit = /[OoQ]/.test(char) ? '0' : /[lI|]/.test(char) ? '1' : null;
      if (digit) {
        chars[start + index] = digit;
        fixed[start + index] = true;
      }
    }
  };
  const letter = /[A-Za-zÄÖÜäöüß]/;
  const pattern = /[0-9OoQlI|]{1,4}(?:[.:][0-9OoQlI|]{1,4}){1,2}[.:]?/g;
  for (const match of text.matchAll(pattern)) {
    const token = match[0];
    const start = match.index;
    if (!/\d/.test(token) || !/[OoQlI|]/.test(token)) continue;
    const before = text[start - 1];
    const after = text[start + token.length];
    if ((before && letter.test(before)) || (after && letter.test(after) && !/^U/.test(after)))
      continue;
    swap(start, token);
  }
  // „l4 Uhr“ — eine Stunde mit falscher Ziffer vor „Uhr“.
  for (const match of text.matchAll(/(?<![A-Za-zÄÖÜäöüß\d])([0-9lI|O]{1,2})(?=\s*Uhr\b)/g)) {
    const token = match[1]!;
    if (/\d/.test(token) && /[lI|O]/.test(token)) swap(match.index, token);
  }
  return { text: chars.join(''), fixed };
}

// --- Gliedern ----------------------------------------------------------------

interface Segment {
  text: string;
  block: number;
  bullet: boolean;
}

const BULLET = /^(?:[-•*–·▪●◦]|\d{1,2}\))\s+/;

function splitSentences(text: string): string[] {
  const out: string[] = [];
  let from = 0;
  for (const match of text.matchAll(/[.!?]\s+(?=[A-ZÄÖÜ„"])/g)) {
    const end = match.index + 1;
    const head = text.slice(from, end);
    const lastWord = (/(\S+)$/.exec(head)?.[1] ?? '').toLowerCase();
    // „5. November“, „z. B.“, „Mi. 4.11.“: kein Satzende.
    if (/^\(?\d{1,2}\.$/.test(lastWord) || ABBREVIATIONS.has(lastWord)) continue;
    if (/^\d{1,2}\.\d{1,2}\.$/.test(lastWord) && /^\s+\d/.test(text.slice(end))) continue;
    out.push(head.trim());
    from = match.index + match[0].length;
  }
  out.push(text.slice(from).trim());
  return out.filter((sentence) => sentence !== '');
}

function segmentsOf(text: string): Segment[] {
  const clean = text
    .replace(/\r\n?/g, '\n')
    .replace(/[\t\u00a0]/g, ' ')
    // Silbentrennung am Zeilenende: „Wander-\ntag“.
    .replace(/([A-Za-zÄÖÜäöüß])[-¬]\n\s*([a-zäöüß])/g, '$1$2');
  const segments: Segment[] = [];
  clean.split(/\n\s*\n/).forEach((block, blockIndex) => {
    let running: string[] = [];
    let listMode = false;
    const flush = () => {
      for (const sentence of splitSentences(running.join(' ')))
        segments.push({ text: sentence, block: blockIndex, bullet: false });
      running = [];
    };
    for (const raw of block.split('\n')) {
      const line = raw.replace(/ {2,}/g, ' ').trim();
      if (line === '') continue;
      if (BULLET.test(line)) {
        flush();
        segments.push({ text: line.replace(BULLET, '').trim(), block: blockIndex, bullet: true });
        continue;
      }
      // Nach „Bitte mitbringen:“ sind kurze Zeilen eine Liste, auch ohne Spiegelstrich.
      if (listMode && line.length <= 50 && !line.endsWith(':')) {
        segments.push({ text: line, block: blockIndex, bullet: true });
        continue;
      }
      running.push(line);
      listMode = line.endsWith(':');
      // „Bitte mitbringen: Jacke, Flasche“ ohne Punkt am Zeilenende: Die Zeile
      // ist hier zu Ende — sonst schluckte die Liste den nächsten Satz samt
      // dessen Datum (27.10.2026 im Browser gefunden).
      if (listMode || (/:\s*\S/.test(line) && !/[.!?,;]$/.test(line))) flush();
    }
    flush();
  });
  return segments;
}

// --- Fundstellen in einem Satz ------------------------------------------------

interface DateMention {
  start: number;
  end: number;
  resolved: Resolved;
  endDate: IsoDate | null;
  role: 'event' | 'deadline';
}

interface TimeMention {
  start: number;
  time: WallTime;
  durationMin: number | null;
}

interface AmountMention {
  start: number;
  end: number;
  cents: number | null;
}

function mask(text: string, start: number, end: number): string {
  return text.slice(0, start) + ' '.repeat(end - start) + text.slice(end);
}

const DEADLINE_BEFORE =
  /(?:\b[Bb]is(?:\s+(?:zum|zur|spätestens|einschließlich))?|\b[Ss]pätestens(?:\s+(?:am|bis))?|\b(?:Abgabe|Rückgabe|Anmeldeschluss|Abgabefrist|Frist|Rückmeldung|Anmeldung)\b(?:\s+(?:bis|am|zum))?)\W*(?:den\s+)?$/;
const DEADLINE_AFTER =
  /^\W*(?:\S+\s+){0,4}?(?:zurück(?:geben|zugeben)?|abgeben|abzugeben|einreichen|einzureichen|eingereicht|abgegeben)\b/;

const NOT_A_DATE_BEFORE =
  /(?:Raum|Zimmer|Tel\.?|Telefon|Fax|Nr\.?|Seite|S\.|Haus|Gebäude|Kapitel|Klasse|Version|Gleis|Linie)\s*$/;

function roleOf(text: string, start: number, end: number): 'event' | 'deadline' {
  const before = text.slice(Math.max(0, start - 30), start);
  const after = text.slice(end, end + 50);
  return DEADLINE_BEFORE.test(before) || DEADLINE_AFTER.test(after) ? 'deadline' : 'event';
}

function findDates(
  input: string,
  fixed: boolean[],
  today: IsoDate,
): { mentions: DateMention[]; masked: string } {
  let text = input;
  const mentions: DateMention[] = [];
  const add = (
    start: number,
    end: number,
    resolved: Resolved | null,
    extra: LetterDoubt[] = [],
    endDate: IsoDate | null = null,
  ) => {
    if (!resolved) return;
    // „Raum 1.05“, „Seite 12.“, „Tel. 12.34“: keine Daten.
    if (NOT_A_DATE_BEFORE.test(input.slice(Math.max(0, start - 12), start))) return;
    const doubts = [...resolved.doubts, ...extra];
    if (fixed.slice(start, end).some(Boolean)) doubts.push('ocrFixed');
    mentions.push({
      start,
      end,
      resolved: { ...resolved, doubts },
      endDate,
      role: roleOf(input, start, end),
    });
    text = mask(text, start, end);
  };
  const run = (pattern: RegExp, handle: (match: RegExpExecArray) => void) => {
    for (const match of [...text.matchAll(pattern)]) handle(match as RegExpExecArray);
  };

  // 1. Zeiträume mit Monatsnamen: „vom 10. bis 14. November“.
  run(
    new RegExp(
      `(?:\\b[Vv]om\\s+)?${WD_PREFIX}(?<!\\d)(\\d{1,2})\\.?\\s*(?:${MONTH}\\.?\\s*)?${DASH}\\s*${WD_PREFIX}(\\d{1,2})\\.?\\s*${MONTH}\\.?(?:\\s*(\\d{4}))?`,
      'g',
    ),
    (match) => {
      const [, wd1, d1, m1, wd2, d2, m2, y] = match;
      const month2 = monthOfName(m2!)!;
      const month1 = m1 ? monthOfName(m1)! : month2;
      const year = y ? Number(y) : null;
      const first = resolveDate(Number(d1), month1, year, weekdayOfName(wd1), today);
      const last = resolveDate(Number(d2), month2, year, weekdayOfName(wd2), today);
      if (!first || !last) return;
      const extra: LetterDoubt[] = [...last.doubts];
      // Über den Jahreswechsel: „28. Dezember bis 3. Januar“.
      let lastDate = last.date;
      if (lastDate < first.date && year === null)
        lastDate = validDate(Number(first.date.slice(0, 4)) + 1, month2, Number(d2)) ?? lastDate;
      if (lastDate < first.date) return;
      add(match.index, match.index + match[0].length, first, extra, lastDate);
    },
  );
  // 2. Zeiträume in Ziffern: „10.–14.11.“, „10.11. – 14.11.2026“.
  run(
    new RegExp(
      `(?:\\b[Vv]om\\s+)?${WD_PREFIX}(?<![\\d.])(\\d{1,2})\\.(?:(\\d{1,2})\\.(\\d{4}|\\d{2}(?!\\d))?)?\\s*${DASH}\\s*${WD_PREFIX}(\\d{1,2})\\.(\\d{1,2})\\.(\\d{4}|\\d{2}(?!\\d))?`,
      'g',
    ),
    (match) => {
      const [, wd1, d1, m1, y1, wd2, d2, m2, y2] = match;
      const year2 = y2 ? Number(y2) : null;
      const year1 = y1 ? Number(y1) : year2;
      const first = resolveDate(
        Number(d1),
        m1 ? Number(m1) : Number(m2),
        year1,
        weekdayOfName(wd1),
        today,
      );
      const last = resolveDate(Number(d2), Number(m2), year2, weekdayOfName(wd2), today);
      if (!first || !last) return;
      let lastDate = last.date;
      if (lastDate < first.date && year2 === null)
        lastDate =
          validDate(Number(first.date.slice(0, 4)) + 1, Number(m2), Number(d2)) ?? lastDate;
      if (lastDate < first.date) return;
      add(match.index, match.index + match[0].length, first, [...last.doubts], lastDate);
    },
  );
  // 3. Mit Monatsnamen: „Mittwoch, 4. November“, „4. Nov. 2026“.
  run(
    new RegExp(`${WD_PREFIX}(?<![\\d.])(\\d{1,2})\\.?\\s*${MONTH}\\.?(?:\\s*(\\d{4}))?`, 'g'),
    (match) => {
      const [, wd, d, m, y] = match;
      const extra: LetterDoubt[] = /rn/.test(m!) ? ['ocrFixed'] : [];
      add(
        match.index,
        match.index + match[0].length,
        resolveDate(Number(d), monthOfName(m!)!, y ? Number(y) : null, weekdayOfName(wd), today),
        extra,
      );
    },
  );
  // 4. In Ziffern: „05.11.“, „5.11.2026“, „5.11.26“ — mit Schlusspunkt oder Jahr.
  run(
    new RegExp(
      `${WD_PREFIX}(?<![\\d.,])(\\d{1,2})\\.\\s?(\\d{1,2})(?:\\.(?:\\s?(\\d{4}|\\d{2}(?![\\d:.])))?|(?=\\s*(?:[,;)]|$)))`,
      'g',
    ),
    (match) => {
      const [, wd, d, m, y] = match;
      // „8.15 Uhr“ ist eine Uhrzeit.
      if (/^\s*Uhr/.test(text.slice(match.index + match[0].length))) return;
      add(
        match.index,
        match.index + match[0].length,
        resolveDate(Number(d), Number(m), y ? Number(y) : null, weekdayOfName(wd), today),
      );
    },
  );
  // 4b. Ohne Schlusspunkt nur nach „am“, „bis“ … oder einem Wochentag: „am 05.11 um“.
  run(
    new RegExp(
      `(?:\\b(?:[Aa]m|[Bb]is|[Vv]om|[Zz]um|[Aa]b|den)\\s+|${WD_FULL}\\.?,?\\s*)(\\d{1,2})\\.(\\d{1,2})(?![\\d.,]|\\s*(?:Uhr|€|Euro|EUR))`,
      'g',
    ),
    (match) => {
      const [, wd, d, m] = match;
      add(
        match.index,
        match.index + match[0].length,
        resolveDate(Number(d), Number(m), null, weekdayOfName(wd), today),
      );
    },
  );
  // 5. Nur ein Tag: „am 12.“ — der Monat ist ergänzt.
  run(
    new RegExp(
      `\\b(?:[Aa]m|[Bb]is(?:\\s+zum)?|[Zz]um|[Aa]b\\s+dem)\\s+(\\d{1,2})\\.(?!\\s*\\d)(?!\\s*${MONTH})(?!\\s*(?:Klasse|Stunde|Schuljahr|Mal))`,
      'g',
    ),
    (match) => {
      const day = Number(match[1]);
      let year = Number(today.slice(0, 4));
      let month = Number(today.slice(5, 7));
      if (day < Number(today.slice(8, 10))) month += 1;
      let date: IsoDate | null = null;
      for (let tries = 0; tries < 3 && !date; tries += 1) {
        if (month > 12) {
          month -= 12;
          year += 1;
        }
        date = validDate(year, month, day);
        if (!date) month += 1;
      }
      if (!date) return;
      add(match.index, match.index + match[0].length, { date, past: false, doubts: [] }, [
        'monthGuessed',
      ]);
    },
  );
  // 6. „nächste Woche Dienstag“.
  run(new RegExp(`\\b[Nn]ächste[n]?\\s+Woche\\s+(?:am\\s+)?${WD_FULL}`, 'g'), (match) => {
    const weekday = weekdayOfName(match[1])!;
    const monday = addDays(today, 8 - weekdayOf(today));
    add(
      match.index,
      match.index + match[0].length,
      { date: addDays(monday, weekday - 1), past: false, doubts: [] },
      ['relativeDay'],
    );
  });
  // 7. „kommenden Montag“, „bis Freitag“, „am Donnerstag“ — nur ein Wochentag.
  run(
    new RegExp(
      `\\b(?:(?:[Aa]m\\s+)?(?:kommenden|nächsten|diesen)|[Bb]is(?:\\s+(?:zum|spätestens))?|[Ss]pätestens(?:\\s+am)?|[Aa]m)\\s+${WD_FULL}\\b`,
      'g',
    ),
    (match) => {
      const weekday = weekdayOfName(match[1])!;
      const until = /^(?:[Bb]is|[Ss]pätestens)/.test(match[0]);
      add(
        match.index,
        match.index + match[0].length,
        { date: nextWeekday(today, weekday, until), past: false, doubts: [] },
        ['weekdayOnly'],
      );
    },
  );

  mentions.sort((a, b) => a.start - b.start);
  return { mentions, masked: text };
}

function hm(hour: string, minute: string | undefined): WallTime | null {
  const h = Number(hour);
  const m = minute ? Number(minute) : 0;
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

function minutesOf(time: WallTime): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

function findTimes(input: string): TimeMention[] {
  let text = input;
  const times: TimeMention[] = [];
  const take = (start: number, length: number, time: WallTime | null, end?: WallTime | null) => {
    text = mask(text, start, start + length);
    if (!time) return;
    // „bis 16 Uhr“ ist ein Ende, kein Beginn.
    if (end === undefined && /\b[Bb]is\s*$/.test(input.slice(Math.max(0, start - 6), start)))
      return;
    const duration = end ? minutesOf(end) - minutesOf(time) : null;
    times.push({
      start,
      time,
      durationMin: duration !== null && duration > 0 ? duration : null,
    });
  };
  for (const match of [
    ...text.matchAll(
      /(?<![\d.:])(\d{1,2})(?:[:.](\d{2}))?\s*(?:Uhr\s*)?(?:-|–|—|bis)\s*(\d{1,2})(?:[:.](\d{2}))?\s*Uhr\b/g,
    ),
  ])
    take(match.index, match[0].length, hm(match[1]!, match[2]), hm(match[3]!, match[4]) ?? null);
  for (const match of [
    ...text.matchAll(/(?<![\d.:])(\d{1,2}):(\d{2})\s*(?:-|–|—|bis)\s*(\d{1,2}):(\d{2})(?!\d)/g),
  ])
    take(match.index, match[0].length, hm(match[1]!, match[2]), hm(match[3]!, match[4]) ?? null);
  for (const match of [...text.matchAll(/(?<![\d.:])(\d{1,2})[:.](\d{2})\s*Uhr\b/g)])
    take(match.index, match[0].length, hm(match[1]!, match[2]));
  for (const match of [...text.matchAll(/(?<![\d.:])(\d{1,2}):(\d{2})(?![\d:])/g)])
    take(match.index, match[0].length, hm(match[1]!, match[2]));
  for (const match of [...text.matchAll(/(?<![\d.:,])(\d{1,2})\s*Uhr\b/g)])
    take(match.index, match[0].length, hm(match[1]!, undefined));
  return times.sort((a, b) => a.start - b.start);
}

function findAmounts(text: string): AmountMention[] {
  const out: AmountMention[] = [];
  const push = (start: number, end: number, raw: string) => {
    const cleaned = raw.replace(/,\s*[-–]$/, '');
    out.push({ start, end, cents: parseAmount(cleaned) });
  };
  for (const match of text.matchAll(
    /(?<![\d.,])(\d{1,5}(?:[.,]\d{1,3})?(?:,\s*[-–])?)\s*(?:€|EUR\b|Euro\b)/g,
  ))
    push(match.index, match.index + match[0].length, match[1]!);
  for (const match of text.matchAll(/(?:€|EUR)\s*(\d{1,5}(?:[.,]\d{1,3})?)(?![\d])/g)) {
    if (out.some((amount) => match.index >= amount.start && match.index < amount.end)) continue;
    push(match.index, match.index + match[0].length, match[1]!);
  }
  return out.sort((a, b) => a.start - b.start);
}

interface Word {
  start: number;
  text: string;
}

function wordsOf(text: string): Word[] {
  return [...text.matchAll(/[A-Za-zÄÖÜäöüßé][A-Za-zÄÖÜäöüßé-]*/g)].map((match) => ({
    start: match.index,
    text: match[0].replace(/-+$/, ''),
  }));
}

function isEventWord(word: string): boolean {
  const lower = word.toLocaleLowerCase('de');
  if (lower.length < 4 || NOT_EVENT.has(lower)) return false;
  if (EVENT_WORDS.has(lower)) return true;
  return EVENT_SUFFIXES.some((suffix) => lower.endsWith(suffix) && lower !== suffix);
}

function nearest<T extends { start: number }>(items: T[], at: number): T | null {
  let best: T | null = null;
  for (const item of items)
    if (!best || Math.abs(item.start - at) < Math.abs(best.start - at)) best = item;
  return best;
}

const CLOSED =
  /\b(Kita|Schule|Hort|Kindergarten|Krippe|Betreuung|OGS)\b(?:\s+\S+){0,4}?\s+geschlossen\b/;

function eventTitle(masked: string, at: number): { title: LetterTitle; guessed: boolean } {
  const closed = CLOSED.exec(masked);
  if (closed) return { title: { type: 'name', text: `${closed[1]} geschlossen` }, guessed: false };
  const keyword = nearest(
    wordsOf(masked).filter((word) => isEventWord(word.text)),
    at,
  );
  if (keyword) return { title: { type: 'name', text: capitalize(keyword.text) }, guessed: false };
  const words = masked
    .replace(/\b(?:Liebe|Sehr geehrte)\s+Eltern\b,?/g, ' ')
    .replace(/\d{1,2}(?:[:.]\d{2})?\s*Uhr\b|\d{1,2}:\d{2}/g, ' ')
    .replace(/[„“"()]/g, ' ')
    .split(/\s+/)
    .map((word) => word.replace(/^[,.;:!?]+|[,.;:!?]+$/g, ''))
    .filter(
      (word) =>
        word !== '' &&
        !/^(?:am|um|ab|bis|vom|dem|den|der|die|das|findet|statt|bitte|wieder|Am|Um|Ab|Bis|Vom|Uhr)$/.test(
          word,
        ),
    );
  let text = '';
  for (const word of words.slice(0, 8)) {
    if (`${text} ${word}`.trim().length > 60) break;
    text = `${text} ${word}`.trim();
  }
  return { title: { type: 'sentence', text: text ? capitalize(text) : '' }, guessed: true };
}

function subjectNear(masked: string, at: number): string | null {
  const candidates = wordsOf(masked).filter((word) => {
    if (!/^[A-ZÄÖÜ]/.test(word.text)) return false;
    const lower = word.text.toLocaleLowerCase('de');
    return SUBJECT_SUFFIXES.some((suffix) => lower.endsWith(suffix));
  });
  return nearest(candidates, at)?.text ?? null;
}

function moneySubject(masked: string, amount: AmountMention): string | null {
  const after = masked.slice(amount.end);
  const forWhat =
    /^\s*(?:pro\s+Kind\s+|je\s+Kind\s+)?(?:für\s+(?:den|die|das|einen|eine)?\s*)([A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]+)/.exec(
      after,
    );
  if (forWhat) return forWhat[1]!;
  const before = masked.slice(0, amount.start);
  const costs = /([A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]+)\s+(?:kostet|beträgt|von|in\s+Höhe\s+von)\s*$/.exec(
    before,
  );
  if (costs) return costs[1]!;
  return null;
}

// --- Mitbringlisten ----------------------------------------------------------

const BRING_COLON =
  /\b(?:[Bb]itte\s+)?(?:mit(?:zu)?bringen|[Mm]itzubringen|mitnehmen|mitzunehmen|mitgeben|[Bb]enötigt\s+(?:wird|werden)|(?:wird|werden)\s+benötigt|[Ii]hr\s+Kind\s+braucht|[Dd]enken\s+Sie\s+(?:bitte\s+)?an|[Dd]enkt\s+(?:bitte\s+)?an|[Ww]ir\s+brauchen)\b(?:\s+(?:sind|ist|werden|bitte))?\s*:\s*(.*)$/;
const BRING_GIVE =
  /\b(?:geben|gebt)\s+(?:Sie\s+)?(?:bitte\s+)?(?:Ihrem|Ihrer|eurem|eurer|dem|den|Ihren)\s+Kind(?:e|ern)?\s+(?:bitte\s+)?(.+?)\s+mit\b/;
const BRING_PLEASE =
  /\b[Bb]itte\s+(?!(?:geben|bringen|denken|melden|beachten|Sie|achten)\b)(.+?)\s+mit(?:bringen|nehmen|geben)\b/;
const BRING_THINK = /\b[Dd]enk(?:en\s+Sie|t)\s+(?:bitte\s+)?an\s+(?!:)(.+?)(?:[.!]|$)/;

function splitItems(text: string): string[] {
  // Nie im Betrag trennen: „8,50 €“ ist eine Zahl, kein Komma zwischen zwei Dingen.
  return text
    .split(/(?<!\d),|,(?!\d)|;|\bund\b|\bsowie\b|\boder\b|\s[-–]\s|•/)
    .map((item) =>
      item
        .replace(/[.!:]+$/g, '')
        .replace(/^\s*(?:bitte|ein|eine|einen|einem|der|die|das|den|dem|ihr|ihre|ihren)\s+/i, '')
        .trim(),
    )
    .filter(
      (item) =>
        item !== '' &&
        item.length <= 60 &&
        /[A-Za-zÄÖÜäöüß]{2,}/.test(item) &&
        !/(?:€|\bEUR\b|\bEuro\b)/.test(item) &&
        !/^(?:viele|liebe|herzliche|beste|mit\s+freundlichen)\s+grüße/i.test(item),
    );
}

// --- Gesamt ------------------------------------------------------------------

interface Draft extends LetterSuggestion {
  segment: number;
  block: number;
  /** Datum aus dem Zusammenhang nachtragen (Geld, Mitbringliste ohne eigenes Datum). */
  needsDate: boolean;
}

function confidenceOf(doubts: LetterDoubt[]): LetterConfidence {
  if (doubts.some((doubt) => LOW_DOUBTS.has(doubt))) return 'low';
  return doubts.length > 0 ? 'medium' : 'high';
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

const LETTER_DATE_PREFIX = /^\s*(?:Datum:?\s*)?(?:[A-ZÄÖÜ][\p{L} .-]{1,40},\s*)?(?:den\s+)?$/u;

/**
 * Vorschläge aus dem Text eines Elternbriefs. `now` und `timeZone` geben
 * „heute“ — gerechnet in der Zeitzone des Nutzers, nie in UTC.
 */
export function parseSchoolLetter(text: string, now: Date, timeZone: string): LetterReading {
  const today = isoDateInZone(now, timeZone);
  const drafts: Draft[] = [];
  const skipped: LetterSkipped[] = [];
  const segments = segmentsOf(text);
  const consumed = new Set<number>();
  /** Uhrzeiten von Sätzen ohne eigenes Datum — „Wir treffen uns um 8:15 Uhr.“ */
  const looseTimes = new Map<number, TimeMention>();
  /** Überschriften mit Stichwort — „Elternbrief: Ausflug in den Tierpark“. */
  const headings = new Map<number, string>();

  segments.forEach((segment, index) => {
    const { text: fixedText, fixed } = fixOcrDigits(segment.text);
    const { mentions, masked } = findDates(fixedText, fixed, today);
    const times = findTimes(masked);
    const amounts = findAmounts(masked);
    const source = clip(segment.text);
    const base = {
      time: null,
      durationMin: null,
      endDate: null,
      amountCents: null,
      items: [] as string[],
      sourceLine: source,
      segment: index,
      block: segment.block,
      needsDate: false,
    };

    const live: DateMention[] = [];
    for (const mention of mentions) {
      const before = fixedText.slice(0, mention.start);
      const after = fixedText.slice(mention.end).trim();
      if (
        mentions.length === 1 &&
        LETTER_DATE_PREFIX.test(before) &&
        /^[.]?$/.test(after) &&
        !segment.bullet
      ) {
        skipped.push({ reason: 'letterDate', date: mention.resolved.date, sourceLine: source });
        continue;
      }
      if (mention.resolved.past && (mention.endDate ?? mention.resolved.date) < today) {
        skipped.push({ reason: 'past', date: mention.resolved.date, sourceLine: source });
        continue;
      }
      live.push(mention);
    }

    if (mentions.length === 0 && times[0]) looseTimes.set(index, times[0]);
    // Eine Überschrift: kurze Zeile ohne Datum, Uhrzeit, Betrag und Satzzeichen am Ende.
    if (
      mentions.length === 0 &&
      times.length === 0 &&
      amounts.length === 0 &&
      !segment.bullet &&
      segment.text.length <= 80 &&
      !/[.!?,;:]$/.test(segment.text)
    ) {
      const keyword = wordsOf(masked).find((word) => isEventWord(word.text));
      if (keyword) headings.set(index, capitalize(keyword.text));
    }

    const deadline = live.find((mention) => mention.role === 'deadline') ?? null;
    const anchor = deadline ?? live[0] ?? null;
    const mergeMoney = amounts.length > 0;

    live.forEach((mention, position) => {
      const nextStart = live[position + 1]?.start ?? Infinity;
      const ownTime =
        times.find((time) => time.start >= mention.end && time.start < nextStart) ??
        (live.length === 1 ? (times[0] ?? null) : null);
      const doubts = [...mention.resolved.doubts];
      if (mention.role === 'deadline') {
        const subject = subjectNear(masked, mention.start);
        const isMoney = mergeMoney && (!subject || /(?:geld|beitrag)$/i.test(subject));
        if (isMoney) return; // Geld mit Frist: der Geld-Vorschlag trägt das Datum.
        drafts.push({
          ...base,
          kind: 'task',
          title: { type: 'deadline', subject },
          date: mention.resolved.date,
          time: ownTime?.time ?? null,
          doubts: subject ? doubts : unique([...doubts, 'titleGuessed']),
          confidence: 'high',
        });
        return;
      }
      const { title, guessed } = eventTitle(masked, mention.start);
      drafts.push({
        ...base,
        kind: 'event',
        title,
        date: mention.resolved.date,
        endDate: mention.endDate,
        time: mention.endDate ? null : (ownTime?.time ?? null),
        durationMin: mention.endDate ? null : (ownTime?.durationMin ?? null),
        doubts: guessed ? unique([...doubts, 'titleGuessed']) : doubts,
        confidence: 'high',
      });
    });

    for (const amount of amounts) {
      const doubts: LetterDoubt[] = anchor ? [...anchor.resolved.doubts] : [];
      if (amount.cents === null) doubts.push('amountUnclear');
      const subject =
        moneySubject(masked, amount) ??
        (() => {
          const named = eventTitle(masked, amount.start);
          return named.title.type === 'name' ? named.title.text : null;
        })();
      drafts.push({
        ...base,
        kind: 'task',
        title: { type: 'money', subject, transfer: /überweis/i.test(masked) },
        date: anchor?.resolved.date ?? null,
        amountCents: amount.cents,
        doubts: unique(doubts),
        confidence: 'high',
        needsDate: !anchor,
      });
    }

    // Mitbringliste.
    if (!consumed.has(index)) {
      let items: string[] = [];
      let sourceLine = source;
      const colon = BRING_COLON.exec(segment.text);
      if (colon) {
        const rest = colon[1]!.trim();
        if (rest !== '') items = splitItems(rest);
        else {
          const lines: string[] = [];
          for (let next = index + 1; next < segments.length; next += 1) {
            const candidate = segments[next]!;
            if (candidate.block !== segment.block || !candidate.bullet) break;
            consumed.add(next);
            lines.push(candidate.text);
          }
          items = lines.flatMap(splitItems);
          if (lines.length > 0) sourceLine = clip(`${segment.text} ${lines.join(', ')}`);
        }
      } else if (amounts.length === 0) {
        // „Bitte geben Sie Ihrem Kind 8,50 € für Eintritt und Bus mit.“ ist Geld,
        // keine Mitbringliste — „Bus“ wäre sonst ein Ding zum Einpacken
        // (28.10.2026 am Gerät gefunden). Eine Liste nach Doppelpunkt bleibt.
        const give = BRING_GIVE.exec(segment.text) ?? BRING_PLEASE.exec(segment.text);
        const think = give ? null : BRING_THINK.exec(segment.text);
        const found = give ?? think;
        if (found) items = splitItems(found[1]!);
      }
      if (items.length > 0) {
        drafts.push({
          ...base,
          sourceLine,
          kind: 'task',
          title: { type: 'bring' },
          date: anchor?.resolved.date ?? null,
          items: unique(items),
          doubts: anchor ? [...anchor.resolved.doubts] : [],
          confidence: 'high',
          needsDate: !anchor,
        });
      }
    }

    // „Bitte geben Sie Ihrem Kind am 4.11. 3 € mit.“ — das Datum gehört zur
    // Aufgabe; ein Termin ohne Stichwort daneben wäre nur Lärm.
    const own = drafts.filter((draft) => draft.segment === index);
    if (own.some((draft) => draft.title.type === 'money' || draft.title.type === 'bring')) {
      for (const draft of own)
        if (draft.kind === 'event' && draft.title.type === 'sentence')
          drafts.splice(drafts.indexOf(draft), 1);
    }
  });

  // Ein einziger Termin ohne Stichwort nimmt es aus der Überschrift davor
  // („Elternbrief: Ausflug in den Tierpark“) — bei mehreren wäre unklar, welcher.
  const unnamed = drafts.filter(
    (draft) => draft.kind === 'event' && draft.title.type === 'sentence',
  );
  if (unnamed.length === 1) {
    const event = unnamed[0]!;
    const heading = [...headings].filter(([at]) => at < event.segment).at(-1);
    if (heading) {
      event.title = { type: 'name', text: heading[1] };
      event.doubts = event.doubts.filter((doubt) => doubt !== 'titleGuessed');
    }
  }

  // Geld und Mitbringliste ohne eigenes Datum: aus dem Absatz, sonst aus dem
  // Brief — nur wenn es dort genau ein Termindatum gibt. Sonst bleibt es leer.
  const eventDates = (filter: (draft: Draft) => boolean) =>
    unique(
      drafts
        .filter((draft) => draft.kind === 'event' && draft.date && filter(draft))
        .map((draft) => draft.date!),
    );
  const letterDates = eventDates(() => true);
  for (const draft of [...drafts]) {
    if (!draft.needsDate) continue;
    // „Kosten: 15 €. Bitte bis 1.12. abgeben.“ — die Frist ohne Gegenstand im
    // nächsten Satz ist die des Geldes.
    if (draft.title.type === 'money') {
      const deadline = drafts.find(
        (other) =>
          other.segment === draft.segment + 1 &&
          other.block === draft.block &&
          other.title.type === 'deadline' &&
          other.title.subject === null,
      );
      if (deadline) {
        draft.date = deadline.date;
        draft.time = deadline.time;
        draft.doubts = unique([
          ...draft.doubts,
          ...deadline.doubts.filter((doubt) => doubt !== 'titleGuessed'),
          'dateFromContext',
        ]);
        drafts.splice(drafts.indexOf(deadline), 1);
        continue;
      }
    }
    // Erst der nächste Termin davor, höchstens drei Sätze zurück („Am 4.11. ist
    // Wandertag. Treffpunkt 8 Uhr. Bitte 3 € mitgeben.“) — die Texterkennung
    // setzt Leerzeilen, wo der Brief keine Absätze hat. Dann der Absatz, dann
    // der Brief, wenn es dort genau ein Termindatum gibt.
    const before = drafts
      .filter(
        (other) =>
          other.kind === 'event' &&
          other.date &&
          other.segment < draft.segment &&
          other.segment >= draft.segment - 3,
      )
      .at(-1);
    // Stehen in jenem Satz zwei Termine, ist es nicht eindeutig.
    const previous = before ? eventDates((other) => other.segment === before.segment) : [];
    const blockDates = eventDates((other) => other.block === draft.block);
    const date =
      previous.length === 1
        ? previous[0]!
        : blockDates.length === 1
          ? blockDates[0]!
          : blockDates.length === 0 && letterDates.length === 1
            ? letterDates[0]!
            : null;
    if (date) {
      const source = drafts.find((other) => other.kind === 'event' && other.date === date);
      draft.date = date;
      draft.doubts = unique([...draft.doubts, ...(source?.doubts ?? []), 'dateFromContext']);
    } else draft.doubts = unique([...draft.doubts, 'noDate']);
  }

  // Ein Termin ohne Uhrzeit nimmt die des nächsten Satzes, wenn der kein Datum hat.
  for (const draft of drafts) {
    const loose = looseTimes.get(draft.segment + 1);
    if (draft.kind !== 'event' || draft.time || draft.endDate || !loose) continue;
    // Auch über eine Leerzeile — die Texterkennung setzt zwischen Zeilen gern eine.
    if ((segments[draft.segment + 1]?.block ?? -1) - draft.block > 1) continue;
    draft.time = loose.time;
    draft.durationMin = loose.durationMin;
  }

  // Doppelt Genanntes („Wandertag am 4.11.“ im Betreff und im Text) einmal.
  const result: LetterSuggestion[] = [];
  for (const draft of drafts) {
    const key = JSON.stringify([draft.kind, draft.title, draft.date, draft.amountCents]);
    const twin = result.find(
      (other) => JSON.stringify([other.kind, other.title, other.date, other.amountCents]) === key,
    );
    const doubts = draft.doubts;
    const suggestion: LetterSuggestion = {
      kind: draft.kind,
      title: draft.title,
      date: draft.date,
      time: draft.time,
      durationMin: draft.durationMin,
      endDate: draft.endDate,
      amountCents: draft.amountCents,
      items: draft.items,
      doubts: unique(doubts),
      confidence: confidenceOf(doubts),
      sourceLine: draft.sourceLine,
    };
    if (twin) {
      twin.time ??= suggestion.time;
      twin.durationMin ??= suggestion.durationMin;
      twin.items = unique([...twin.items, ...suggestion.items]);
      if (twin.doubts.length > suggestion.doubts.length) {
        twin.doubts = suggestion.doubts;
        twin.confidence = suggestion.confidence;
      }
      continue;
    }
    result.push(suggestion);
  }

  const seen = new Set<string>();
  return {
    suggestions: result,
    skipped: skipped.filter((entry) => {
      const key = `${entry.reason}|${entry.date}|${entry.sourceLine}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
  };
}
